/**
 * IconSelector Component
 * 
 * Icon selector with searchable icon grid
 * Used by CategoryForm
 */

import React, { useMemo, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { Check, Search } from 'lucide-react';
import clsx from 'clsx';

interface IconSelectorProps {
  value?: string;
  onChange: (icon?: string) => void;
  label?: string;
  helperText?: string;
  error?: string;
  disabled?: boolean;
}

interface IconGroup {
  id: string;
  label: string;
  icons: string[];
}

const ICON_GROUPS: IconGroup[] = [
  {
    id: 'essentials',
    label: 'Essentials',
    icons: [
      'ShoppingCart',
      'Home',
      'Wallet',
      'CreditCard',
      'PiggyBank',
      'DollarSign',
      'Briefcase',
      'Building',
      'Receipt',
      'ClipboardList',
      'Tag',
      'Gift',
    ],
  },
  {
    id: 'lifestyle',
    label: 'Lifestyle',
    icons: [
      'Coffee',
      'Utensils',
      'Wine',
      'Plane',
      'Sun',
      'Music',
      'Film',
      'Gamepad2',
      'ShoppingBag',
      'Sparkles',
      'Palette',
      'Camera',
    ],
  },
  {
    id: 'transport',
    label: 'Transport',
    icons: [
      'Car',
      'Bus',
      'Bike',
      'Train',
      'Truck',
      'Ship',
      'Plane',
      'Navigation',
      'Fuel',
      'ParkingSquare',
      'MapPin',
      'Compass',
    ],
  },
  {
    id: 'housing',
    label: 'Home & Utilities',
    icons: [
      'Bed',
      'Couch',
      'Lamp',
      'Droplet',
      'Flame',
      'Thermometer',
      'Router',
      'SparkPlug',
      'Hammer',
      'Wrench',
      'Lightbulb',
      'ShieldCheck',
    ],
  },
  {
    id: 'health',
    label: 'Health & Wellness',
    icons: [
      'Heart',
      'Stethoscope',
      'FirstAidKit',
      'Capsule',
      'Syringe',
      'Dumbbell',
      'Apple',
      'Sprout',
      'Heartbeat',
      'Lotus',
      'Droplets',
      'Shield',
    ],
  },
  {
    id: 'work',
    label: 'Work & Tools',
    icons: [
      'Monitor',
      'Laptop',
      'Printer',
      'Calculator',
      'Files',
      'BookOpenCheck',
      'GraduationCap',
      'PenTool',
      'HardHat',
      'Factory',
      'Scale',
      'Users',
    ],
  },
];

const isLucideIconExport = (value: unknown): boolean => {
  if (!value) return false;
  const valueType = typeof value;
  if (valueType === 'function') return true;
  if (valueType === 'object') {
    // forwardRef components in React are objects with render/displayName
    return 'render' in (value as Record<string, unknown>) || 'displayName' in (value as Record<string, unknown>);
  }
  return false;
};

const ALL_ICON_NAMES = Object.keys(LucideIcons).filter((name) => {
  if (name === 'createLucideIcon' || name.startsWith('Icon')) {
    return false;
  }
  return isLucideIconExport((LucideIcons as any)[name]);
});

const toPascalCase = (value: string): string =>
  value
    .split(/[-_\s]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');

const formatIconLabel = (name: string): string =>
  name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export function IconSelector({
  value,
  onChange,
  label = 'Icon',
  helperText,
  error,
  disabled = false,
}: IconSelectorProps): React.ReactElement {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<string>(ICON_GROUPS[0]?.id ?? '');

  const selectedIconPascal = value ? toPascalCase(value) : undefined;
  const SelectedIcon =
    selectedIconPascal && isLucideIconExport((LucideIcons as any)[selectedIconPascal])
      ? (LucideIcons as any)[selectedIconPascal]
      : null;

  const filteredIcons = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    if (query.length > 0) {
      return ALL_ICON_NAMES.filter((iconName) => {
        const kebab = iconName
          .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
          .toLowerCase();
        return iconName.toLowerCase().includes(query) || kebab.includes(query);
      }).slice(0, 60);
    }

    const activeIcons =
      ICON_GROUPS.find((group) => group.id === activeGroup)?.icons ?? [];

    return activeIcons.filter((iconName) => isLucideIconExport((LucideIcons as any)[iconName]));
  }, [searchQuery, activeGroup]);

  const handleSelectIcon = (iconName: string) => {
    onChange(iconName);
  };

  const handleClear = () => {
    onChange(undefined);
    setSearchQuery('');
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>

      {/* Selected icon preview */}
      <div className="flex items-center gap-3">
        <div
          className={clsx(
            'flex h-12 w-12 items-center justify-center rounded-lg border text-gray-600 dark:text-gray-300',
            SelectedIcon
              ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
              : 'border-dashed border-gray-300 dark:border-gray-600'
          )}
        >
          {SelectedIcon ? (
            <SelectedIcon className="h-6 w-6" aria-hidden="true" />
          ) : (
            <span className="text-xs text-gray-400">No icon</span>
          )}
        </div>

        <div className="flex-1">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {SelectedIcon ? formatIconLabel(selectedIconPascal ?? '') : 'Select an icon'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Icons come from the Lucide set. Pick the one that best represents your category.
          </p>
        </div>

        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className={`text-sm ${
            disabled
              ? 'text-gray-400 cursor-not-allowed dark:text-gray-500'
              : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
          }`}
          aria-label="Clear icon selection"
          aria-disabled={disabled}
        >
          Clear
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${disabled ? 'text-gray-300 dark:text-gray-600' : 'text-gray-400'}`} aria-hidden="true" />
        <input
          type="text"
          value={searchQuery}
          onChange={(event) => !disabled && setSearchQuery(event.target.value)}
          placeholder="Search icons (e.g. shopping, travel, health)"
          disabled={disabled}
          className={`w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-3 text-gray-900 shadow-sm outline-none transition ${
            disabled
              ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
              : 'focus:border-primary-400 focus:ring-2 focus:ring-primary-500'
          } dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100`}
          aria-label="Search icons"
          aria-disabled={disabled}
        />
      </div>

      {/* Group tabs */}
      {searchQuery.trim() === '' && (
        <div className="flex flex-wrap gap-2">
          {ICON_GROUPS.map((group) => (
            <button
              key={group.id}
              type="button"
              onClick={() => !disabled && setActiveGroup(group.id)}
              disabled={disabled}
              className={clsx(
                'rounded-full px-3 py-1 text-xs font-medium transition',
                disabled
                  ? 'opacity-60 cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'
                  : activeGroup === group.id
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
              )}
              aria-pressed={activeGroup === group.id}
              aria-disabled={disabled}
            >
              {group.label}
            </button>
          ))}
        </div>
      )}

      {/* Icon grid */}
      <div className="grid max-h-64 grid-cols-6 gap-2 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700">
        {filteredIcons.length === 0 ? (
          <p className="col-span-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            No icons found. Try a different search term.
          </p>
        ) : (
          filteredIcons.map((iconName) => {
            const Icon = (LucideIcons as any)[iconName];
            if (!isLucideIconExport(Icon)) return null;

            const isSelected =
              selectedIconPascal !== undefined &&
              iconName === selectedIconPascal;

            return (
              <button
                key={iconName}
                type="button"
                onClick={() => !disabled && handleSelectIcon(iconName)}
                disabled={disabled}
                className={clsx(
                  'relative flex flex-col items-center justify-center rounded-lg border-2 px-2 py-3 text-xs transition',
                  disabled
                    ? 'opacity-60 cursor-not-allowed border-transparent bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-500'
                    : isSelected
                    ? 'border-primary-500 bg-primary-50 text-primary-600 dark:border-primary-500 dark:bg-primary-900/20 dark:text-primary-300'
                    : 'border-transparent bg-white text-gray-600 hover:border-primary-300 hover:bg-primary-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-500 dark:hover:bg-primary-900/10'
                )}
                aria-label={`Select ${formatIconLabel(iconName)} icon`}
                aria-pressed={isSelected}
                aria-disabled={disabled}
              >
                <Icon className="mb-1 h-5 w-5" aria-hidden="true" />
                <span className="truncate text-[11px] leading-tight">
                  {formatIconLabel(iconName)}
                </span>
                {isSelected && (
                  <Check className="absolute right-1 top-1 h-3 w-3" aria-hidden="true" />
                )}
              </button>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>
          {filteredIcons.length} {filteredIcons.length === 1 ? 'icon' : 'icons'} shown
        </span>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className={`${
            disabled
              ? 'text-gray-400 cursor-not-allowed dark:text-gray-500'
              : 'text-primary-600 hover:underline dark:text-primary-400'
          }`}
          aria-disabled={disabled}
        >
          Set to none
        </button>
      </div>

      {helperText && (
        <p className="text-xs text-gray-500 dark:text-gray-400">{helperText}</p>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

